import React, {
  useState,
  useMemo,
  useEffect,
  useCallback,
  ChangeEvent,
} from "react";
import { useTranslation } from "react-i18next";
import { Row, SortingRule } from "react-table";
import { HvGrid, ListValueProp } from "@hitachivantara/uikit-react-core";

import {
  HvTableColumnConfig,
  HvRowInstance,
  HvCellProps,
} from "@hitachivantara/uikit-react-lab";

import {
  DEFAULT_PAGE,
  textColumn,
  switchColumn,
  SwitchColumnCell,
} from "components/common/SimpleTable";
import { useUsers } from "lib/api";
import UserTable from "components/common/UserTable/UserTable";
import {
  joinGroups,
  addMember,
  removeMember,
  extractSelection,
} from "../../../../common/UserTable/utils";

import useMembersFilters from "../../../../common/UserTable/useMembersFilters";

export interface MembersTableProps {
  assignee?: UserWithGroup;
  members?: UserWithGroup[];
  initialPageSize?: number;
  groupFilter?: ListValueProp[];
  searchKey?: string;
  viewOption?: "all" | "member" | "default";
  onMembersChange?: (members: UserWithGroup[]) => void;
  onAssigneeChange?: (assignee: UserWithGroup | undefined) => void;
}

const MembersTable: React.FC<MembersTableProps> = ({
  assignee,
  members,
  searchKey,
  groupFilter,
  initialPageSize = 10,
  viewOption = "all",
  onAssigneeChange,
  onMembersChange,
}) => {
  const { t } = useTranslation("incidentAddEdit");
  const [tableData, setTableData] = useState<MemberDataRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);

  const [size, setSize] = useState(initialPageSize);
  const SWITCH_ID_SUFFIX = "switch";
  const [globalFilter, filterGroups] = useMembersFilters();
  const onSelectMemberHandler = useCallback(
    (
      event: React.FormEvent<HTMLTableRowElement> | undefined,
      row: HvRowInstance<MemberDataRow>
    ) => {
      const inputElement = event?.target as HTMLInputElement;
      if (inputElement.id.includes(SWITCH_ID_SUFFIX)) return;
      const isChecked = inputElement.checked;
      const rowMember = {
        id: row.original.id,
        email: row.original.email,
        name: row.original.name,
        surname: row.original.surname,
        group: row.original.group,
        username: row.original.username,
        is_integration: row.original.is_integration,
        enabled: row.original.enabled,
      } as UserWithGroup;
      if (!isChecked) {
        const newMembers = removeMember(rowMember, members || []);
        onMembersChange?.(newMembers);
        row.toggleRowSelected?.(isChecked);
        if (row.original.id === assignee?.id) onAssigneeChange?.(undefined);
      } else {
        const newMembers = addMember(rowMember, members || []);
        onMembersChange?.(newMembers);
        row.toggleRowSelected?.(isChecked);
      }
    },
    [assignee?.id, members, onAssigneeChange, onMembersChange]
  );
  const onSelectAssigneeHandler = useCallback(
    (
      event: Event,
      checked: boolean,
      val: string,
      row: HvRowInstance<UserWithGroup>
    ) => {
      if (val !== assignee?.id) {
        const newAssignee = {
          id: row.original.id,
          email: row.original.email,
          name: row.original.name,
          surname: row.original.surname,
          group: row.original.group,
        } as UserWithGroup;
        onAssigneeChange?.(newAssignee);
        const newMembers = addMember(newAssignee, members);
        onMembersChange?.(newMembers);
        row.toggleRowSelected(true);
        return;
      }
      onAssigneeChange?.(undefined);
    },
    [assignee?.id, onAssigneeChange, onMembersChange, members]
  );

  const columns = useMemo(
    (): HvTableColumnConfig<MemberDataRow>[] => [
      switchColumn(
        {
          Header: t("members.columns.assignee") as string,
          accessor: "assignee",
          disableSortBy: true,
          sortType: "alphanumeric",
          style: {
            width: "80px",
            padding: "8px 0 0 8px",
          },
          Cell: ({ value, row }: HvCellProps<UserWithGroup, boolean>) => {
            return (
              <SwitchColumnCell
                checked={value}
                value={row.id}
                switchLabel={t("members.labels.assigneeSwitch") as string}
                switchProps={{
                  id: `${row.original.email}-${SWITCH_ID_SUFFIX}`,
                  onChange: (event, checked, val: string) =>
                    onSelectAssigneeHandler(event, checked, val, row),
                  readOnly: false,
                }}
              />
            );
          },
        },
        t("members.columns.assignee"),
        undefined,
        undefined
      ),
      textColumn({
        Header: t("members.columns.email") as string,
        accessor: "email",
        style: { width: "30%" },
      }),
      textColumn({
        Header: t("members.columns.name") as string,
        accessor: "name",
        style: { width: "15%" },
      }),
      textColumn({
        Header: t("members.columns.surname") as string,
        accessor: "surname",
        style: { width: "15%" },
      }),
      textColumn({
        Header: t("members.columns.group") as string,
        accessor: "group",
        disableGlobalFilter: true,
        filter: filterGroups,
      }),
    ],
    [t, onSelectAssigneeHandler, filterGroups]
  );

  const handleSelectAll = useCallback(
    (
      event: React.MouseEvent<Element, MouseEvent>,
      visibleRows: HvRowInstance<MemberDataRow>[],
      isAllPageRowsSelected: boolean
    ) => {
      if (members === undefined) return;
      let newMembers = [...members];
      if (isAllPageRowsSelected) {
        visibleRows.forEach((row) => {
          const rowMember = {
            id: row.original.id,
            email: row.original.email,
            name: row.original.name,
            surname: row.original.surname,
            group: row.original.group,
            username: row.original.username,
            is_integration: row.original.is_integration,
            enabled: row.original.enabled,
          } as UserWithGroup;
          newMembers = removeMember(rowMember, newMembers);
          row.toggleRowSelected?.(false);
        });
        onAssigneeChange?.(undefined);
        onMembersChange?.(newMembers);
      } else {
        visibleRows.forEach((row) => {
          const rowMember = {
            id: row.original.id,
            email: row.original.email,
            name: row.original.name,
            surname: row.original.surname,
            username: row.original.username,
            is_integration: row.original.is_integration,
            enabled: row.original.enabled,
          } as UserWithGroup;
          newMembers = addMember(rowMember, newMembers);
          row.toggleRowSelected?.(true);
        });
        onMembersChange?.(newMembers);
      }
    },
    [members, onAssigneeChange, onMembersChange]
  );

  const handleSelectAllPages = useCallback(
    (
      event: ChangeEvent<HTMLInputElement>,
      rows: Row<MemberDataRow>[],
      toggleAllRowsSelected?: (value?: boolean | undefined) => void
    ) => {
      if (members === undefined) return;
      let newMembers = [...members];
      rows.forEach((row) => {
        const rowMember = {
          id: row.original.id,
          email: row.original.email,
          name: row.original.name,
          surname: row.original.surname,
          username: row.original.username,
          is_integration: row.original.is_integration,
          enabled: row.original.enabled,
        } as UserWithGroup;
        newMembers = addMember(rowMember, newMembers);
      });
      toggleAllRowsSelected?.(true);
      onMembersChange?.(newMembers);
    },
    [members, onMembersChange]
  );

  const [sortBy, setSortBy] = useState<SortingRule<MemberDataRow>[]>([
    {
      id: "email",
      desc: false,
    },
  ]);
  const { data: userData, error: membersError } = useUsers(
    {
      shouldRetryOnError: false,
    },
    {
      pageNr: -1,
      pageSize: -1,
      includeGroups: "true",
      isExcludeServiceAccount: "true",
    }
  );

  useEffect(() => {
    if (userData) {
      const data = userData.data.map((member) => ({
        id: member.id,
        assignee: member.email === assignee?.email,
        email: member.email,
        name: member.name,
        surname: member.surname,
        username: member.username,
        is_integration: member.is_integration,
        enabled: member.enabled,
        group: joinGroups(member.groups_details),
      })) as MemberDataRow[];
      setTableData(data);
      setTotalCount(userData?.totalCount || 0);
    }
  }, [userData, assignee]);

  const selectedRowIds = extractSelection(members);

  const state = useMemo(() => {
    return {
      pageIndex: page,
      pageSize: size,
      sortBy,

      selectedRowIds,
    };
  }, [sortBy, page, size, selectedRowIds]);

  const onGlobalFilter = useCallback(
    (
      rows: Row<MemberDataRow>[],
      columnIds: string[],
      filterValue: MembersTableFilter
    ) => globalFilter(rows, columnIds, filterValue, members),
    [globalFilter, members]
  );
  const globalFilterValues = {
    searchKey: searchKey || "",
    viewOption,
  } as MembersTableFilter;

  useEffect(() => {
    setPage(DEFAULT_PAGE);
  }, [searchKey]);

  return (
    <HvGrid item container spacing={2}>
      <HvGrid item xs={12}>
        <UserTable
          columns={columns}
          rowId="id"
          data={tableData}
          totalCount={totalCount}
          initialState={state}
          onGlobalFilter={onGlobalFilter}
          groupsKeys={groupFilter || []}
          globalFilterValues={{ ...globalFilterValues }}
          setPage={setPage}
          setSize={setSize}
          setSortBy={setSortBy}
          loading={(!userData && !membersError) || tableData === []}
          onChangeRow={onSelectMemberHandler}
          onSelectAll={handleSelectAll}
          onSelectAllPages={handleSelectAllPages}
        />
      </HvGrid>
    </HvGrid>
  );
};
export default MembersTable;
